import React, { FC, useState, useMemo, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Spin, Switch, Modal, notification } from 'antd';
import {
  LoadingOutlined,
  CheckOutlined,
  CloseOutlined,
  LeftOutlined,
  RightOutlined,
  FileTextOutlined,
} from '@ant-design/icons';
import toast from 'react-hot-toast';
import { useQuery } from '@tanstack/react-query';
import { doc, getDoc } from 'firebase/firestore';
import { auth, db } from 'src/config/firebase';
import { sessionStore } from 'src/store/sessionStore';
import {
  useConfirmCheckinMutation,
  useCompleteCheckinMutation,
  useZoomMeetingStatusQuery,
  useSuggestCheckinMutation,
  useDeclineCheckinMutation,
  useSendCheckinReminderMutation,
  useUpdateCheckinActionItemsMutation,
  useMarkCheckinReadMutation,
  CheckinActionItem,
  CheckinStatus,
  CheckinProgress,
  CheckinSelectTimeType,
  CheckinType,
} from 'src/api/checkins';
import { useCheckinRealtime } from 'src/hooks/useCheckinRealtime';
import { useCheckinsRealtime, CheckinSlotRaw } from 'src/hooks/useCheckinsRealtime';
import { useGetTrainerClientById } from 'src/api/mutations/useGetTrainerClients';
import { Button } from 'src/components/Button/Button';
import { ReactComponent as DashboardModalArchiveIcon } from 'src/assets/images/trainer/clients/dashboard-modal-archive.svg';
import { ReactComponent as DashboardModalDuplicateIcon } from 'src/assets/images/trainer/clients/dashboard-modal-duplicate.svg';
import { ReactComponent as CheckinConfirmIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/checin-request-confirm.svg';
import { ReactComponent as CheckinDeclineIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/checin-request-decline.svg';
import { ReactComponent as CheckinSuggestIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/checin-request-suggest-new-time.svg';
import { ReactComponent as CheckinRequestSentIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/checkin-request-sent.svg';
import { ReactComponent as ConfirmedDetailsIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-details.svg';
import { ReactComponent as ConfirmedMeetingAccessIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-meeting-access.svg';
import { ReactComponent as ConfirmedCopyLinkIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-copy-link.svg';
import { ReactComponent as ConfirmedOpenZoomIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-open-zoom.svg';
import { ReactComponent as ConfirmedAiProgressIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-ai-progress.svg';
import { ReactComponent as ConfirmedPerformanceOverviewIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-performance-overview.svg';
import { ReactComponent as ConfirmedWorkoutCompletedIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-workout-completed.svg';
import { ReactComponent as ConfirmedLastActivityIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-last-activity.svg';
import { ReactComponent as ConfirmedFormConcernIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-form-concern.svg';
import { ReactComponent as ConfirmedNextActionsIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-next-actions.svg';
import { ReactComponent as ConfirmedSendReminderIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-send-reminder.svg';
import { ReactComponent as ConfirmedRescheduleIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-confirmed-reschedule-check-in.svg';
import { ReactComponent as ConfirmedTranscriptionIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-completed-transcription.svg';
import { ReactComponent as CompletedActionItemsIcon } from 'src/assets/images/onboarding-trainer/dashboard/checkins/confirmed/check-in-completed-action-items.svg';
import { ReactComponent as ChatBackArrowIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/notifications/chat-back-arrow.svg';
import { ReactComponent as EmptyCheckinsIcon } from 'src/assets/images/onboarding-trainer/dashboard/messages/empty-checkins.svg';
import { ReactComponent as SimpleLineCheckIcon } from 'src/assets/images/onboarding-trainer/dashboard/program-generator-ai/components/simple-line-check.svg';
import './CheckinDetails.scss';

interface CheckinDetailsProps {
  checkinId: string;
  onBack: () => void;
}

interface TimeSlotOption extends CheckinSlotRaw {
  display: string;
  value: string; // 24-hr format "HH:MM"
}

const formatTimeWithAmPm = (timeStr: string) => {
  if (!timeStr) return '4:00 PM';
  const clean = timeStr.trim();
  const ampmMatch = clean.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!ampmMatch) {
    const parts = clean.split(':');
    if (parts.length < 2) return clean;
    let hour = parseInt(parts[0], 10);
    const min = parts[1].replace(/[^\d]/g, '');
    if (isNaN(hour)) return clean;
    const ampm = hour >= 12 ? 'PM' : 'AM';
    hour = hour % 12 || 12;
    return `${hour}:${min.padStart(2, '0')} ${ampm}`;
  }
  let hour = parseInt(ampmMatch[1], 10);
  const min = ampmMatch[2];
  const ampmStr = ampmMatch[3]?.toUpperCase();
  if (ampmStr) {
    return `${hour % 12 || 12}:${min} ${ampmStr}`;
  }
  const ampm = hour >= 12 ? 'PM' : 'AM';
  hour = hour % 12 || 12;
  return `${hour}:${min} ${ampm}`;
};

export const normalizeTo24HourTime = (timeStr?: string): string => {
  if (!timeStr) return '';
  const clean = timeStr.trim();
  const ampmMatch = clean.match(/^(\d{1,2}):(\d{2})(?::\d{2})?\s*(AM|PM)?$/i);
  if (!ampmMatch) {
    if (/^\d{1,2}:\d{2}/.test(clean)) {
      const [h, m] = clean.split(':');
      return `${String(parseInt(h, 10)).padStart(2, '0')}:${m.slice(0, 2)}`;
    }
    return clean;
  }
  let hours = parseInt(ampmMatch[1], 10);
  const minutes = ampmMatch[2];
  const ampm = ampmMatch[3]?.toUpperCase();

  if (ampm === 'PM' && hours < 12) {
    hours += 12;
  } else if (ampm === 'AM' && hours === 12) {
    hours = 0;
  }

  return `${String(hours).padStart(2, '0')}:${minutes}`;
};

export const CheckinDetails: FC<CheckinDetailsProps> = ({ checkinId, onBack }) => {
  const navigate = useNavigate();
  const currentTrainerUid = auth.currentUser?.uid || sessionStore.getState().currentUser?.uid || '';
  const { checkins: allTrainerCheckins, checkinDaysMap } = useCheckinsRealtime(currentTrainerUid);

  const matchedFromList = useMemo(() => {
    return allTrainerCheckins.find(
      (c) => c.id === checkinId || c.message_id === checkinId || c.checkin_id === checkinId,
    );
  }, [allTrainerCheckins, checkinId]);

  const derivedChatId =
    matchedFromList?.chat_id ||
    (matchedFromList?.client_uid
      ? `${currentTrainerUid}_${matchedFromList.client_uid}`
      : undefined);

  const { checkin, loading } = useCheckinRealtime(checkinId, derivedChatId, currentTrainerUid);

  const clientUid = checkin?.client_uid || checkin?.appointUId || '';
  const { data: clientQueryData } = useGetTrainerClientById(clientUid);
  const matchedClient = clientQueryData?.clients?.[0];

  const { data: flutterUserData } = useQuery({
    queryKey: ['flutter-client-profile', clientUid],
    queryFn: async () => {
      if (!clientUid) return null;
      try {
        const uSnap = await getDoc(doc(db, 'flutter_user', clientUid));
        if (uSnap.exists()) {
          return uSnap.data() as {
            email?: string;
            name?: string;
            surname?: string;
            avatar_url?: string;
            avatar?: string;
            profile_picture?: string;
          };
        }
      } catch (err) {
        console.warn('Failed to fetch flutter_user:', err);
      }
      return null;
    },
    enabled: !!clientUid,
    staleTime: 5 * 60 * 1000,
  });

  const clientName =
    checkin?.client_name && checkin.client_name !== 'Client' && checkin.client_name !== 'Guest'
      ? checkin.client_name
      : matchedClient
        ? `${matchedClient.name} ${matchedClient.surname}`.trim()
        : flutterUserData?.name
          ? `${flutterUserData.name} ${flutterUserData.surname || ''}`.trim()
          : checkin?.client_name && checkin.client_name !== 'Client'
            ? checkin.client_name
            : 'Guest';

  const clientEmail = checkin?.client_email || matchedClient?.email || flutterUserData?.email || '';

  const clientAvatar =
    checkin?.client_avatar ||
    matchedClient?.avatarUrl ||
    flutterUserData?.avatar_url ||
    flutterUserData?.avatar ||
    flutterUserData?.profile_picture ||
    null;

  const clientInitials =
    checkin?.client_initials &&
    checkin.client_initials !== 'CL' &&
    checkin.client_initials.length >= 2
      ? checkin.client_initials
      : (() => {
          const name = clientName && clientName !== 'Client' ? clientName : 'Guest';
          const parts = name.trim().split(/\s+/);
          if (parts.length > 1) {
            return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
          }
          return name.slice(0, 2).toUpperCase();
        })();

  const handleClientBannerClick = () => {
    if (clientUid) {
      navigate(`/onboarding-trainer/dashboard/clients/${clientUid}`);
    }
  };

  // Local date-time bounds (based on user's local timezone)
  const SCHEDULE_DAYS_AHEAD = 60;
  const now = useMemo(() => new Date(), []);
  const today = useMemo(() => new Date(now.getFullYear(), now.getMonth(), now.getDate()), [now]);
  const maxDate = useMemo(() => {
    const d = new Date(today);
    d.setDate(d.getDate() + SCHEDULE_DAYS_AHEAD);
    d.setHours(23, 59, 59, 999);
    return d;
  }, [today]);

  const minMonthDate = useMemo(() => new Date(today.getFullYear(), today.getMonth(), 1), [today]);
  const maxMonthDate = useMemo(
    () => new Date(maxDate.getFullYear(), maxDate.getMonth(), 1),
    [maxDate],
  );

  const markReadMutation = useMarkCheckinReadMutation();
  // Mark checkin as read when opened by trainer via backend API
  useEffect(() => {
    if (!checkinId || !checkin || checkin.is_read) return;
    markReadMutation.mutate({
      checkinId,
      targetDate: checkin.date,
      chatId: checkin.chat_id,
    });
  }, [checkinId, checkin, markReadMutation]);

  const [view, setView] = useState<'details' | 'suggest' | 'success'>('details');
  const [sendZoomLink, setSendZoomLink] = useState<boolean>(true);

  // Always reset scroll to top on view or checkin navigation
  useEffect(() => {
    const checkinContainer = document.querySelector('.checkin-details-container');
    if (checkinContainer) {
      checkinContainer.scrollTop = 0;
    }
    const dashboardMain = document.getElementById('scrollableDashboardMain');
    if (dashboardMain) {
      dashboardMain.scrollTop = 0;
    }
    window.scrollTo(0, 0);
  }, [view, checkinId]);

  // Modal states
  const [isFullTranscriptModalOpen, setIsFullTranscriptModalOpen] = useState(false);
  const [isDeclineModalOpen, setIsDeclineModalOpen] = useState(false);
  const [declineReason, setDeclineReason] = useState<string>('');
  const [declineReasonError, setDeclineReasonError] = useState<string>('');
  const [isConfirmAlreadySentModalOpen, setIsConfirmAlreadySentModalOpen] = useState(false);
  const [isSuggestAlreadySentModalOpen, setIsSuggestAlreadySentModalOpen] = useState(false);
  const [isCompleteModalOpen, setIsCompleteModalOpen] = useState(false);

  // Complete modal form states
  const [completionAttendance, setCompletionAttendance] = useState<'attended' | 'missed'>(
    'attended',
  );
  const [completionDuration, setCompletionDuration] = useState<number>(30);
  const [completionNotes, setCompletionNotes] = useState<string>('');
  const [newActionItemText, setNewActionItemText] = useState<string>('');
  const [completionActionItems, setCompletionActionItems] = useState<CheckinActionItem[]>([]);

  const currentActionItems = useMemo<CheckinActionItem[]>(() => {
    return checkin?.action_items || [];
  }, [checkin?.action_items]);

  // Action items checkbox state
  const [actionItemsChecked, setActionItemsChecked] = useState<{ [key: string]: boolean }>({});

  // Sync action items checked state from checkin
  useEffect(() => {
    if (currentActionItems && currentActionItems.length > 0) {
      const checkedMap: { [key: string]: boolean } = {};
      currentActionItems.forEach((item) => {
        checkedMap[item.id] = item.completed;
      });
      setActionItemsChecked(checkedMap);
    }
  }, [currentActionItems]);

  // Suggest time calendar state (defaults to current month and day in local timezone)
  const [currentMonthDate, setCurrentMonthDate] = useState<Date>(
    () => new Date(now.getFullYear(), now.getMonth(), 1),
  );
  const [selectedDay, setSelectedDay] = useState<number>(() => now.getDate());
  const [selectedTimeSlot, setSelectedTimeSlot] = useState<string>('09:30');

  // Sync calendar selection to current check-in's date and time when opened or navigated to suggest view
  useEffect(() => {
    if (checkin?.date) {
      const parts = checkin.date.split('-');
      if (parts.length === 3) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) - 1;
        const d = parseInt(parts[2], 10);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          const checkinDate = new Date(y, m, d);
          if (checkinDate >= today && checkinDate <= maxDate) {
            setCurrentMonthDate(new Date(y, m, 1));
            setSelectedDay(d);
            const targetTime = normalizeTo24HourTime(checkin.time || checkin.checkinTime);
            if (targetTime) {
              setSelectedTimeSlot(targetTime);
            }
          }
        }
      }
    }
  }, [checkin?.date, checkin?.time, checkin?.checkinTime, today, maxDate, view]);

  // Mutations
  const confirmMutation = useConfirmCheckinMutation();
  const completeMutation = useCompleteCheckinMutation();
  const suggestMutation = useSuggestCheckinMutation();
  const declineMutation = useDeclineCheckinMutation();
  const sendReminderMutation = useSendCheckinReminderMutation();
  const updateActionItemsMutation = useUpdateCheckinActionItemsMutation();

  const isZoomCheckin =
    checkin?.preference === 'online_zoom' || !!checkin?.zoom_link || !!checkin?.meeting_id;

  const { data: zoomStatus, isLoading: isZoomStatusLoading } = useZoomMeetingStatusQuery(
    checkinId,
    checkin?.chat_id,
    isCompleteModalOpen && isZoomCheckin,
  );

  useEffect(() => {
    if (zoomStatus?.actual_duration_mins && isCompleteModalOpen) {
      setCompletionDuration(zoomStatus.actual_duration_mins);
    }
  }, [zoomStatus?.actual_duration_mins, isCompleteModalOpen]);

  const durationOptions = useMemo(() => {
    const base = [15, 30, 45, 60];
    if (zoomStatus?.actual_duration_mins && !base.includes(zoomStatus.actual_duration_mins)) {
      return [...base, zoomStatus.actual_duration_mins].sort((a, b) => a - b);
    }
    return base;
  }, [zoomStatus?.actual_duration_mins]);

  const handleOpenCompleteModal = () => {
    setCompletionAttendance('attended');
    setCompletionDuration(checkin?.duration_mins || 30);
    setCompletionNotes('');
    setNewActionItemText('');
    setCompletionActionItems([]);
    setIsCompleteModalOpen(true);
  };
  void handleOpenCompleteModal;

  const handleOpenReschedule = () => {
    if (checkin?.date) {
      const parts = checkin.date.split('-');
      if (parts.length === 3) {
        const y = parseInt(parts[0], 10);
        const m = parseInt(parts[1], 10) - 1;
        const d = parseInt(parts[2], 10);
        if (!isNaN(y) && !isNaN(m) && !isNaN(d)) {
          const checkinDate = new Date(y, m, d);
          if (checkinDate >= today && checkinDate <= maxDate) {
            setCurrentMonthDate(new Date(y, m, 1));
            setSelectedDay(d);
          }
        }
      }
    }
    const targetTime = normalizeTo24HourTime(checkin?.time || checkin?.checkinTime);
    if (targetTime) {
      setSelectedTimeSlot(targetTime);
    }
    setView('suggest');
  };

  const handleAddActionItem = () => {
    const trimmed = newActionItemText.trim();
    if (!trimmed) return;
    setCompletionActionItems((prev) => [
      ...prev,
      { id: String(Date.now()), text: trimmed, completed: false },
    ]);
    setNewActionItemText('');
  };

  const handleRemoveActionItem = (id: string) => {
    setCompletionActionItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleCompleteSubmit = async () => {
    if (!checkinId) return;
    try {
      await completeMutation.mutateAsync({
        checkinId,
        chatId: checkin?.chat_id,
        attendance: completionAttendance,
        durationMins: completionDuration,
        notes: completionNotes.trim(),
        actionItems: completionActionItems,
      });

      setIsCompleteModalOpen(false);
      toast.success('Check-in marked as completed!');
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to complete check-in';
      toast.error(msg);
    }
  };

  const showNotificationSentToast = useCallback(() => {
    notification.open({
      title: (
        <div className="custom-toast-content checkin-notification-toast-content">
          <div className="toast-left">
            <div className="toast-header">
              <SimpleLineCheckIcon className="toast-icon" />
              <span className="toast-text">Notification Sent</span>
            </div>
            <div className="toast-progress-track">
              <div className="toast-progress-bar" />
            </div>
          </div>
        </div>
      ),
      placement: 'topRight',
      duration: 4,
      className: 'checkin-notification-toast',
      closeIcon: <div />,
    });
  }, []);

  const handleSendReminder = async () => {
    if (!checkin?.id) return;
    try {
      await sendReminderMutation.mutateAsync({
        checkinId: checkin.id,
        chatId: checkin.chat_id,
      });
      showNotificationSentToast();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Failed to send reminder.';
      toast.error(msg);
    }
  };

  const toggleActionItem = async (itemId: string) => {
    const newCompleted = !actionItemsChecked[itemId];
    setActionItemsChecked((prev) => ({ ...prev, [itemId]: newCompleted }));

    const updatedList = currentActionItems.map((item) =>
      item.id === itemId ? { ...item, completed: newCompleted } : item,
    );

    if (checkin?.id) {
      try {
        await updateActionItemsMutation.mutateAsync({
          checkinId: checkin.id,
          chatId: checkin.chat_id,
          actionItems: updatedList,
        });
      } catch (e) {
        console.error('Error updating action items:', e);
      }
    }
  };

  // Meeting ID and Passcode pre-computed by backend
  const meetingId = checkin?.meeting_id || '';
  const passcode = checkin?.passcode || '';

  const [isCopiedZoomLink, setIsCopiedZoomLink] = useState(false);

  const handleCopyZoomLink = () => {
    const url = checkin?.zoom_link || '';
    if (!url) return;
    navigator.clipboard.writeText(url);
    setIsCopiedZoomLink(true);
    setTimeout(() => {
      setIsCopiedZoomLink(false);
    }, 2000);
  };

  // Safe dynamic AI progress summary
  const progressSummary = useMemo(() => {
    return checkin?.ai_progress_summary || null;
  }, [checkin?.ai_progress_summary]);

  // Highlights and summary fallbacks
  const highlights = useMemo(() => {
    return checkin?.transcript_highlights || [];
  }, [checkin?.transcript_highlights]);

  const aiSummaryParagraphs = useMemo(() => {
    return checkin?.ai_summary || [];
  }, [checkin?.ai_summary]);

  const fullTranscriptDialogue = useMemo(() => {
    return checkin?.full_transcript || [];
  }, [checkin?.full_transcript]);

  // Presentation values pre-computed by backend
  const formattedDate =
    checkin?.formatted_date ||
    (checkin?.date
      ? new Date(`${checkin.date}T00:00:00`).toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
        })
      : '');
  const formattedTime =
    checkin?.formatted_time ||
    (checkin?.time
      ? (() => {
          const [hStr, mStr] = checkin.time.split(':');
          const h = parseInt(hStr, 10);
          const m = mStr || '00';
          if (isNaN(h)) return checkin.time;
          const period = h >= 12 ? 'PM' : 'AM';
          const displayH = h % 12 === 0 ? 12 : h % 12;
          return `${displayH}:${m} ${period}`;
        })()
      : '');
  const preferenceLabel =
    checkin?.preference_label ||
    (checkin?.preference === 'in_person' ? 'In-person' : 'Online via Zoom');

  const rawStatus = checkin?.status;
  const checkinProgress: CheckinProgress | undefined = checkin?.checkinProgress;
  const status: CheckinStatus =
    (rawStatus as string) === 'open' || !rawStatus ? 'pending_confirmation' : rawStatus;
  const isNewTimeProposal =
    checkinProgress === '2' ||
    checkin?.status_badge?.label === 'New Time Proposed' ||
    checkin?.status_badge?.label === 'Client Counter-Proposal' ||
    checkin?.status_badge?.className === 'new_time_proposed';

  const defaultBadgeInfo = isNewTimeProposal
    ? {
        label: checkin?.checkinNewSuggest ? 'Client Counter-Proposal' : 'New Time Proposed',
        className: 'new_time_proposed',
        hasDot: true,
      }
    : {
        label: 'Pending confirmation',
        className: 'pending_confirmation',
        hasDot: true,
      };

  const statusBadgeInfo = checkin?.status_badge
    ? {
        ...checkin.status_badge,
        className: isNewTimeProposal ? 'new_time_proposed' : checkin.status_badge.className,
      }
    : defaultBadgeInfo;

  // Check if a suggestion was already sent by the trainer
  const hasAlreadySuggested = Boolean(
    checkinProgress === '2' ||
    checkin?.checkinNewDate ||
    checkin?.suggested_date ||
    (checkin?.status_updated_by && checkin.status_updated_by === auth.currentUser?.uid),
  );

  // Handle Confirm Click
  const handleConfirmClick = () => {
    if (hasAlreadySuggested) {
      setIsConfirmAlreadySentModalOpen(true);
    } else {
      executeConfirm();
    }
  };

  const executeConfirm = async () => {
    if (!checkinId) return;
    try {
      await confirmMutation.mutateAsync({
        checkinId,
        chatId: checkin?.chat_id,
        sendZoomLinkOnConfirm: sendZoomLink,
      });

      setIsConfirmAlreadySentModalOpen(false);
      toast.success('Check-in confirmed successfully!');
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to confirm check-in';
      toast.error(errorMsg);
    }
  };

  // Month navigation bounds
  const canGoPrev = useMemo(() => {
    return (
      currentMonthDate.getFullYear() > minMonthDate.getFullYear() ||
      (currentMonthDate.getFullYear() === minMonthDate.getFullYear() &&
        currentMonthDate.getMonth() > minMonthDate.getMonth())
    );
  }, [currentMonthDate, minMonthDate]);

  const canGoNext = useMemo(() => {
    return (
      currentMonthDate.getFullYear() < maxMonthDate.getFullYear() ||
      (currentMonthDate.getFullYear() === maxMonthDate.getFullYear() &&
        currentMonthDate.getMonth() < maxMonthDate.getMonth())
    );
  }, [currentMonthDate, maxMonthDate]);

  // Is the currently selected day today in local time
  const isSelectedDateToday = useMemo(() => {
    return (
      currentMonthDate.getFullYear() === now.getFullYear() &&
      currentMonthDate.getMonth() === now.getMonth() &&
      selectedDay === now.getDate()
    );
  }, [currentMonthDate, selectedDay, now]);

  // Map of date string "YYYY-MM-DD" -> Set of booked times "HH:MM" for this trainer
  // Map of date string "YYYY-MM-DD" -> Array of booked slot info
  const bookedSlotsDetailsMap = useMemo(() => {
    const map: Record<
      string,
      Array<{
        time: string;
        formattedTime: string;
        clientName?: string;
        isCurrentCheckin?: boolean;
      }>
    > = {};
    (allTrainerCheckins || []).forEach((item) => {
      // Only count active bookings (ignore declined check-ins)
      if (item.status === 'declined') {
        return;
      }
      const itemDate = item.date;
      const itemTime = normalizeTo24HourTime(item.time || item.checkinTime);
      if (itemDate && itemTime && (item.isAppoint || Boolean(item.appointUId))) {
        if (!map[itemDate]) {
          map[itemDate] = [];
        }
        if (!map[itemDate].some((s) => s.time === itemTime)) {
          const formatted = formatTimeWithAmPm(itemTime);

          const currentNormalized = normalizeTo24HourTime(checkin?.time || checkin?.checkinTime);
          const isCurrent =
            item.id === checkinId ||
            item.checkin_id === checkinId ||
            item.message_id === checkinId ||
            (checkin?.id && (item.id === checkin.id || item.checkin_id === checkin.id)) ||
            (checkin?.date === itemDate && currentNormalized === itemTime);

          const rawClient = item.client_name || (isCurrent ? clientName : undefined);
          const displayClient = isCurrent
            ? `${rawClient && rawClient !== 'Client' ? rawClient : 'This Booking'} (This Booking)`
            : rawClient;

          map[itemDate].push({
            time: itemTime,
            formattedTime: formatted,
            clientName: displayClient,
            isCurrentCheckin: isCurrent,
          });
        }
      }
    });

    Object.keys(map).forEach((dateKey) => {
      map[dateKey].sort((a, b) => a.time.localeCompare(b.time));
    });

    return map;
  }, [
    allTrainerCheckins,
    checkinId,
    checkin?.id,
    checkin?.date,
    checkin?.time,
    checkin?.checkinTime,
    clientName,
  ]);

  // Map of date string "YYYY-MM-DD" -> Set of booked times "HH:MM" for fast lookup
  const bookedSlotsMap = useMemo(() => {
    const map: Record<string, Set<string>> = {};
    Object.keys(bookedSlotsDetailsMap).forEach((dateKey) => {
      map[dateKey] = new Set(bookedSlotsDetailsMap[dateKey].map((s) => s.time));
    });
    return map;
  }, [bookedSlotsDetailsMap]);

  // Selected date formatted as "YYYY-MM-DD"
  const selectedDateStr = useMemo(() => {
    const year = currentMonthDate.getFullYear();
    const month = String(currentMonthDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDay).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }, [currentMonthDate, selectedDay]);

  const selectedDayDoc = checkinDaysMap[selectedDateStr];

  const morningSlots = useMemo<TimeSlotOption[]>(() => {
    return (selectedDayDoc?.morningList || []).map((s) => ({
      ...s,
      display: formatTimeWithAmPm(s.time),
      value: normalizeTo24HourTime(s.time),
    }));
  }, [selectedDayDoc]);

  const daytimeSlots = useMemo<TimeSlotOption[]>(() => {
    return (selectedDayDoc?.daytimeList || []).map((s) => ({
      ...s,
      display: formatTimeWithAmPm(s.time),
      value: normalizeTo24HourTime(s.time),
    }));
  }, [selectedDayDoc]);

  const eveningSlots = useMemo<TimeSlotOption[]>(() => {
    return (selectedDayDoc?.eveningList || []).map((s) => ({
      ...s,
      display: formatTimeWithAmPm(s.time),
      value: normalizeTo24HourTime(s.time),
    }));
  }, [selectedDayDoc]);

  const allSelectedDateSlots = useMemo<TimeSlotOption[]>(() => {
    return [...morningSlots, ...daytimeSlots, ...eveningSlots];
  }, [morningSlots, daytimeSlots, eveningSlots]);

  // Check if a specific time slot is already booked for this trainer on the selected date
  const isSlotBooked = useCallback(
    (slotValue: string, slotObj?: CheckinSlotRaw) => {
      if (slotObj?.isAppoint || slotObj?.appointUId) return true;
      const normalizedSlot = normalizeTo24HourTime(slotValue);
      return Boolean(bookedSlotsMap[selectedDateStr]?.has(normalizedSlot));
    },
    [bookedSlotsMap, selectedDateStr],
  );

  // Check if a specific time slot is the current check-in being viewed
  const isCurrentBookingSlot = useCallback(
    (slotValue: string) => {
      if (!checkin?.date) return false;
      const targetTime = normalizeTo24HourTime(checkin.time || checkin.checkinTime);
      const curSlotTime = normalizeTo24HourTime(slotValue);
      return selectedDateStr === checkin.date && Boolean(curSlotTime) && curSlotTime === targetTime;
    },
    [checkin?.date, checkin?.time, checkin?.checkinTime, selectedDateStr],
  );

  // Check if a specific time slot is booked by ANOTHER client on the selected date
  const isSlotBookedByOther = useCallback(
    (slotValue: string, slotObj?: CheckinSlotRaw) => {
      if (isCurrentBookingSlot(slotValue)) {
        return false;
      }
      if (slotObj?.isAppoint || slotObj?.appointUId) return true;
      const normalizedSlot = normalizeTo24HourTime(slotValue);
      return Boolean(bookedSlotsMap[selectedDateStr]?.has(normalizedSlot));
    },
    [bookedSlotsMap, selectedDateStr, isCurrentBookingSlot],
  );

  // Check if a specific time slot is disabled (past, closed, or booked by another client)
  const isSlotDisabled = useCallback(
    (slotValue: string, slotObj?: CheckinSlotRaw) => {
      const selectedDate = new Date(
        currentMonthDate.getFullYear(),
        currentMonthDate.getMonth(),
        selectedDay,
      );
      if (selectedDate < today || selectedDate > maxDate) {
        return true;
      }
      if (slotObj?.status === 'close') {
        return true;
      }
      if (isSlotBookedByOther(slotValue, slotObj)) {
        return true;
      }
      if (isSelectedDateToday) {
        const [hStr, mStr] = slotValue.split(':');
        const h = parseInt(hStr, 10);
        const m = parseInt(mStr || '0', 10);
        const currentHours = now.getHours();
        const currentMinutes = now.getMinutes();
        if (h < currentHours || (h === currentHours && m <= currentMinutes)) {
          return true;
        }
      }
      return false;
    },
    [currentMonthDate, selectedDay, today, maxDate, isSlotBookedByOther, isSelectedDateToday, now],
  );

  // Calendar grid calculations
  const calendarDays = useMemo(() => {
    const year = currentMonthDate.getFullYear();
    const month = currentMonthDate.getMonth();

    const firstDayIndex = new Date(year, month, 1).getDay(); // 0 = Sun, 1 = Mon ...
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    const daysInPrevMonth = new Date(year, month, 0).getDate();

    // In Europe/UK/spec calendar starts on Monday (Mo, Tu, We, Th, Fr, Sa, Su)
    const startOffset = (firstDayIndex + 6) % 7;

    const days: {
      day: number;
      isCurrentMonth: boolean;
      isDisabled: boolean;
      isPast?: boolean;
      isCellToday?: boolean;
      hasGeneratedSlots?: boolean;
      hasBookings: boolean;
      isFullyBooked: boolean;
      bookedSlots: Array<{ time: string; formattedTime: string; clientName?: string }>;
    }[] = [];

    // Preceding month padding
    for (let i = startOffset - 1; i >= 0; i--) {
      days.push({
        day: daysInPrevMonth - i,
        isCurrentMonth: false,
        isDisabled: true,
        hasBookings: false,
        isFullyBooked: false,
        bookedSlots: [],
      });
    }

    // Current month days with past, 60-day boundary, generated checkinList presence, and booking checks
    for (let i = 1; i <= daysInMonth; i++) {
      const cellDate = new Date(year, month, i);
      const isPast = cellDate < today;
      const isBeyondMax = cellDate > maxDate;

      const cellMonthStr = String(month + 1).padStart(2, '0');
      const cellDayStr = String(i).padStart(2, '0');
      const cellDateStr = `${year}-${cellMonthStr}-${cellDayStr}`;

      const dayDoc = checkinDaysMap[cellDateStr];
      const morning = dayDoc?.morningList || [];
      const daytime = dayDoc?.daytimeList || [];
      const evening = dayDoc?.eveningList || [];
      const allDaySlots = [...morning, ...daytime, ...evening];
      const hasGeneratedSlots = allDaySlots.length > 0;

      const bookedSlotsForDay = bookedSlotsDetailsMap[cellDateStr] || [];
      const bookedCount = bookedSlotsForDay.length;
      const hasBookings = bookedCount > 0;

      // Count open available future slots on this cell
      const isCellToday =
        cellDate.getFullYear() === now.getFullYear() &&
        cellDate.getMonth() === now.getMonth() &&
        cellDate.getDate() === now.getDate();

      const currentHours = now.getHours();
      const currentMinutes = now.getMinutes();

      let validOpenSlotsCount = 0;
      allDaySlots.forEach((slot) => {
        const isAppointed =
          slot.isAppoint || Boolean(slot.appointUId) || bookedSlotsMap[cellDateStr]?.has(slot.time);
        if (isAppointed) return;
        if (slot.status === 'close') return;
        if (isCellToday) {
          const [hStr, mStr] = slot.time.split(':');
          const h = parseInt(hStr, 10);
          const m = parseInt(mStr || '0', 10);
          if (h < currentHours || (h === currentHours && m <= currentMinutes)) {
            return;
          }
        }
        validOpenSlotsCount++;
      });

      const isFullyBooked = hasGeneratedSlots && validOpenSlotsCount === 0;
      const isPastDay = isPast && !isCellToday;
      const isDisabled = isBeyondMax || (!hasGeneratedSlots && !isCellToday);

      days.push({
        day: i,
        isCurrentMonth: true,
        isDisabled,
        isPast: isPastDay,
        isCellToday,
        hasGeneratedSlots,
        hasBookings,
        isFullyBooked,
        bookedSlots: bookedSlotsForDay,
      });
    }

    // Trailing month padding to ensure a fixed 6-row calendar (42 days total)
    const TOTAL_CALENDAR_CELLS = 42;
    let nextMonthDay = 1;
    while (days.length < TOTAL_CALENDAR_CELLS) {
      days.push({
        day: nextMonthDay++,
        isCurrentMonth: false,
        isDisabled: true,
        hasBookings: false,
        isFullyBooked: false,
        bookedSlots: [],
      });
    }

    return days;
  }, [
    currentMonthDate,
    today,
    maxDate,
    checkinDaysMap,
    bookedSlotsDetailsMap,
    bookedSlotsMap,
    now,
  ]);

  // 1. Auto-adjust selectedDay when navigating months or when availability loads
  useEffect(() => {
    const daysInMonth = new Date(
      currentMonthDate.getFullYear(),
      currentMonthDate.getMonth() + 1,
      0,
    ).getDate();

    let validDay = selectedDay;
    if (validDay > daysInMonth) {
      validDay = daysInMonth;
    }

    if (checkin?.date) {
      const parts = checkin.date.split('-');
      if (parts.length === 3) {
        const checkinYear = parseInt(parts[0], 10);
        const checkinMonth = parseInt(parts[1], 10) - 1;
        const checkinDay = parseInt(parts[2], 10);
        if (
          currentMonthDate.getFullYear() === checkinYear &&
          currentMonthDate.getMonth() === checkinMonth &&
          selectedDay === checkinDay
        ) {
          const checkinCell = calendarDays.find(
            (d) => d.isCurrentMonth && d.day === checkinDay && !d.isDisabled,
          );
          if (checkinCell) return;
        }
      }
    }

    const currentCell = calendarDays.find(
      (d) => d.isCurrentMonth && d.day === validDay && !d.isDisabled,
    );

    if (!currentCell) {
      // Find the first enabled day in this month
      const firstEnabled = calendarDays.find((d) => d.isCurrentMonth && !d.isDisabled);
      if (firstEnabled) {
        setSelectedDay(firstEnabled.day);
      }
    } else if (validDay !== selectedDay) {
      setSelectedDay(validDay);
    }
  }, [currentMonthDate, calendarDays, selectedDay, checkin?.date]);

  // 2. Auto-adjust selectedTimeSlot if current slot is past, booked, or not in selected date
  useEffect(() => {
    if (allSelectedDateSlots.length === 0) return;

    const currentCheckinTime = normalizeTo24HourTime(checkin?.time || checkin?.checkinTime);
    const isCurrentBookingOnThisDate =
      Boolean(checkin?.date && currentCheckinTime) && selectedDateStr === checkin?.date;

    const isCurrentSlotValid =
      Boolean(selectedTimeSlot) &&
      allSelectedDateSlots.some(
        (s) =>
          normalizeTo24HourTime(s.value) === normalizeTo24HourTime(selectedTimeSlot) &&
          !isSlotDisabled(s.value, s),
      );

    if (!isCurrentSlotValid) {
      if (
        isCurrentBookingOnThisDate &&
        allSelectedDateSlots.some(
          (s) =>
            normalizeTo24HourTime(s.value) === currentCheckinTime && !isSlotDisabled(s.value, s),
        )
      ) {
        setSelectedTimeSlot(currentCheckinTime);
      } else {
        const firstValidSlot = allSelectedDateSlots.find((s) => !isSlotDisabled(s.value, s));
        if (firstValidSlot) {
          setSelectedTimeSlot(firstValidSlot.value);
        } else {
          setSelectedTimeSlot('');
        }
      }
    }
  }, [
    allSelectedDateSlots,
    selectedTimeSlot,
    isSlotDisabled,
    checkin?.date,
    checkin?.time,
    checkin?.checkinTime,
    selectedDateStr,
  ]);

  const monthYearLabel = useMemo(() => {
    return currentMonthDate.toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  }, [currentMonthDate]);

  // Validate Suggest Inputs
  const validateSuggestInput = () => {
    const chosenDate = new Date(
      currentMonthDate.getFullYear(),
      currentMonthDate.getMonth(),
      selectedDay,
    );
    if (chosenDate < today) {
      toast.error('Cannot select a past date.');
      return false;
    }
    if (chosenDate > maxDate) {
      toast.error('Cannot select a date more than 60 days in advance.');
      return false;
    }

    const chosenSlot = allSelectedDateSlots.find((s) => s.value === selectedTimeSlot);
    if (!chosenSlot) {
      toast.error('Please select an available time slot.');
      return false;
    }
    if (isSlotBooked(chosenSlot.value, chosenSlot)) {
      toast.error(
        'Selected time slot is already booked with another client. Please choose an open slot.',
      );
      return false;
    }
    if (isSlotDisabled(chosenSlot.value, chosenSlot)) {
      toast.error(
        'Selected time slot has already passed or is unavailable. Please pick an upcoming time.',
      );
      return false;
    }
    return true;
  };

  // Handle Suggest Submit
  const handleSuggestSubmit = async () => {
    if (!checkinId) return;
    if (!validateSuggestInput()) return;

    if (hasAlreadySuggested) {
      setIsSuggestAlreadySentModalOpen(true);
    } else {
      executeSuggest();
    }
  };

  const executeSuggest = async () => {
    if (!checkinId) return;
    const year = currentMonthDate.getFullYear();
    const month = String(currentMonthDate.getMonth() + 1).padStart(2, '0');
    const day = String(selectedDay).padStart(2, '0');
    const suggestedDateStr = `${year}-${month}-${day}`;

    const hour = parseInt(selectedTimeSlot.split(':')[0], 10);
    const selectTimeType: CheckinSelectTimeType =
      hour < 12 ? 'morningList' : hour < 17 ? 'daytimeList' : 'eveningList';
    const checkinType: CheckinType =
      checkin?.checkinType === 'inPerson' || checkin?.preference === 'in_person'
        ? 'inPerson'
        : 'online';

    try {
      await suggestMutation.mutateAsync({
        checkinId,
        chatId: checkin?.chat_id,
        suggestedDate: suggestedDateStr,
        suggestedTime: selectedTimeSlot,
        suggestedSelectTimeType: selectTimeType,
        suggestedType: checkinType,
        timezone: checkin?.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
      });
      setIsSuggestAlreadySentModalOpen(false);
      setView('success');
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to suggest new time';
      toast.error(errorMsg);
    }
  };

  const validateDeclineReason = (val: string) => {
    if (val.length > 200) {
      return 'Message must be 200 characters or less';
    }
    return '';
  };

  const handleCloseDeclineModal = () => {
    if (declineMutation.isPending) return;
    setIsDeclineModalOpen(false);
    setDeclineReason('');
    setDeclineReasonError('');
  };

  // Handle Decline Submit
  const handleDeclineSubmit = async () => {
    if (!checkinId) return;
    const trimmedReason = declineReason.trim();
    const error = validateDeclineReason(trimmedReason);
    if (error) {
      setDeclineReasonError(error);
      return;
    }

    try {
      await declineMutation.mutateAsync({
        checkinId,
        chatId: checkin?.chat_id,
        reason: trimmedReason || undefined,
      });

      handleCloseDeclineModal();
      toast.success('Check-in declined');
    } catch (err: unknown) {
      const errorMsg = err instanceof Error ? err.message : 'Failed to decline check-in';
      toast.error(errorMsg);
    }
  };

  const renderSuggestAlreadySentModal = () => (
    <Modal
      open={isSuggestAlreadySentModalOpen}
      footer={null}
      onCancel={() => setIsSuggestAlreadySentModalOpen(false)}
      centered
      width={400}
      closeIcon={null}
      rootClassName="logout-modal"
      className="logout-modal"
      styles={{
        mask: {
          backdropFilter: 'blur(4px)',
          backgroundColor: 'rgba(6, 7, 35, 0.8)',
        },
      }}
    >
      <div className="logout-modal-content">
        <div className="logout-icon-wrapper">
          <DashboardModalDuplicateIcon />
        </div>
        <h2>Send New Suggestion?</h2>
        <p>
          You have already sent a time proposal to <b>{checkin?.client_name || 'the client'}</b>. Do
          you want to replace it and propose this new date and time (
          <b>
            {`${currentMonthDate.toLocaleDateString('en-US', { month: 'short' })} ${selectedDay}, ${currentMonthDate.getFullYear()} at ${formatTimeWithAmPm(selectedTimeSlot)}`}
          </b>
          )?
        </p>
        <div className="logout-actions">
          <Button
            className="btn-logout"
            onClick={executeSuggest}
            disabled={suggestMutation.isPending}
            isLoading={suggestMutation.isPending}
          >
            Send New Suggestion
          </Button>
          <Button
            className="btn-cancel"
            onClick={() => setIsSuggestAlreadySentModalOpen(false)}
            disabled={suggestMutation.isPending}
          >
            Cancel
          </Button>
        </div>
      </div>
    </Modal>
  );

  // 1. Success Screen View (Request Sent)
  if (view === 'success') {
    return (
      <div className="checkin-details-container">
        <div className="success-view-container">
          <div className="success-icon-wrapper">
            <CheckinRequestSentIcon />
          </div>
          <h2>Request sent!</h2>
          <p>
            Your client will review your request and confirm the check-in. You&apos;ll receive a
            notification once it&apos;s approved.
          </p>
          <button className="btn-back-messages" onClick={onBack}>
            Back to Messages
          </button>
        </div>
      </div>
    );
  }

  // 2. Suggest New Time Screen View
  if (view === 'suggest') {
    const isSubmitDisabled =
      suggestMutation.isPending ||
      !selectedTimeSlot ||
      (() => {
        const chosen = allSelectedDateSlots.find((s) => s.value === selectedTimeSlot);
        return chosen ? isSlotDisabled(chosen.value, chosen) : true;
      })();

    return (
      <div className="checkin-details-container">
        <div className="details-header">
          <div className="header-left">
            <button className="back-btn" onClick={() => setView('details')} aria-label="Go Back">
              <ChatBackArrowIcon />
            </button>
            <h1>Suggest New Time</h1>
          </div>
        </div>

        <div className="details-body">
          <div className="suggest-time-container">
            {/* Calendar Card */}
            <div className="calendar-card">
              <div className="calendar-header">
                <span className="month-title">{monthYearLabel}</span>
                <div className="nav-arrows">
                  <button
                    type="button"
                    disabled={!canGoPrev}
                    onClick={() => {
                      if (canGoPrev) {
                        setCurrentMonthDate(
                          new Date(
                            currentMonthDate.getFullYear(),
                            currentMonthDate.getMonth() - 1,
                            1,
                          ),
                        );
                      }
                    }}
                    aria-label="Previous Month"
                  >
                    <LeftOutlined />
                  </button>
                  <button
                    type="button"
                    disabled={!canGoNext}
                    onClick={() => {
                      if (canGoNext) {
                        setCurrentMonthDate(
                          new Date(
                            currentMonthDate.getFullYear(),
                            currentMonthDate.getMonth() + 1,
                            1,
                          ),
                        );
                      }
                    }}
                    aria-label="Next Month"
                  >
                    <RightOutlined />
                  </button>
                </div>
              </div>

              <div className="weekdays-grid">
                <span>Mo</span>
                <span>Tu</span>
                <span>We</span>
                <span>Th</span>
                <span>Fr</span>
                <span>Sa</span>
                <span>Su</span>
              </div>

              <div className="days-grid">
                {calendarDays.map((item, idx) => {
                  const isSelected = item.isCurrentMonth && item.day === selectedDay;
                  const hasBookings =
                    item.hasBookings && item.bookedSlots && item.bookedSlots.length > 0;
                  const isTopRows = idx < 14;

                  return (
                    <div key={idx} className="day-cell-wrapper">
                      <button
                        type="button"
                        disabled={item.isDisabled || !item.isCurrentMonth}
                        className={`day-cell ${!item.isCurrentMonth ? 'outside-month' : ''} ${
                          item.isPast ? 'past-day' : ''
                        } ${item.hasBookings ? 'has-bookings' : ''} ${
                          item.isFullyBooked ? 'fully-booked' : ''
                        } ${isSelected ? 'selected' : ''}`}
                        onClick={() => {
                          if (item.isCurrentMonth && !item.isDisabled) {
                            setSelectedDay(item.day);
                          }
                        }}
                      >
                        <span>{item.day}</span>
                        {item.hasBookings && <span className="calendar-booking-dot" />}
                      </button>

                      {/* Hover Popup for Booked Times */}
                      {hasBookings && item.isCurrentMonth && (
                        <div
                          className={`booked-slots-tooltip ${isTopRows ? 'position-bottom' : ''}`}
                        >
                          <div className="tooltip-header">
                            <span className="tooltip-title">
                              Booked Times ({item.bookedSlots.length})
                            </span>
                            {item.isFullyBooked && <span className="tooltip-badge">Full</span>}
                          </div>
                          <div className="tooltip-slots-list">
                            {item.bookedSlots.map((bSlot, sIdx) => (
                              <div key={sIdx} className="tooltip-slot-item">
                                <span className="slot-dot" />
                                <span className="slot-time">{bSlot.formattedTime}</span>
                                {bSlot.clientName && (
                                  <span className="slot-client">· {bSlot.clientName}</span>
                                )}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Time Slots Section */}
            <div className="time-slots-section">
              {allSelectedDateSlots.length === 0 ? (
                <div
                  className="empty-slots-message"
                  style={{
                    padding: '20px',
                    textAlign: 'center',
                    background: '#111422',
                    borderRadius: '12px',
                    border: '1px solid rgba(255, 255, 255, 0.06)',
                    color: '#8e95a5',
                    fontSize: '14px',
                  }}
                >
                  No available time slots configured for this date.
                </div>
              ) : (
                <>
                  {morningSlots.length > 0 && (
                    <div className="slot-group">
                      <h4>Morning</h4>
                      <div className="chips-grid">
                        {morningSlots.map((slot) => {
                          const isCurrent = isCurrentBookingSlot(slot.value);
                          const disabled = isSlotDisabled(slot.value, slot);
                          const booked = isSlotBookedByOther(slot.value, slot);
                          return (
                            <button
                              key={slot.value}
                              type="button"
                              disabled={disabled}
                              className={`time-chip ${disabled ? 'disabled' : ''} ${booked ? 'booked' : ''} ${
                                isCurrent ? 'current-booking' : ''
                              } ${selectedTimeSlot === slot.value && !disabled ? 'selected' : ''}`}
                              onClick={() => {
                                if (!disabled) setSelectedTimeSlot(slot.value);
                              }}
                              title={
                                isCurrent
                                  ? 'Current booking time (This Booking)'
                                  : booked
                                    ? slot.clientName || 'Already booked with another client'
                                    : undefined
                              }
                            >
                              <span>{slot.display}</span>
                              {isCurrent ? (
                                <span className="booked-tag current-tag">This Booking</span>
                              ) : booked ? (
                                <span className="booked-tag">Booked</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {daytimeSlots.length > 0 && (
                    <div className="slot-group">
                      <h4>Daytime</h4>
                      <div className="chips-grid">
                        {daytimeSlots.map((slot) => {
                          const isCurrent = isCurrentBookingSlot(slot.value);
                          const disabled = isSlotDisabled(slot.value, slot);
                          const booked = isSlotBookedByOther(slot.value, slot);
                          return (
                            <button
                              key={slot.value}
                              type="button"
                              disabled={disabled}
                              className={`time-chip ${disabled ? 'disabled' : ''} ${booked ? 'booked' : ''} ${
                                isCurrent ? 'current-booking' : ''
                              } ${selectedTimeSlot === slot.value && !disabled ? 'selected' : ''}`}
                              onClick={() => {
                                if (!disabled) setSelectedTimeSlot(slot.value);
                              }}
                              title={
                                isCurrent
                                  ? 'Current booking time (This Booking)'
                                  : booked
                                    ? slot.clientName || 'Already booked with another client'
                                    : undefined
                              }
                            >
                              <span>{slot.display}</span>
                              {isCurrent ? (
                                <span className="booked-tag current-tag">This Booking</span>
                              ) : booked ? (
                                <span className="booked-tag">Booked</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}

                  {eveningSlots.length > 0 && (
                    <div className="slot-group">
                      <h4>Evening</h4>
                      <div className="chips-grid">
                        {eveningSlots.map((slot) => {
                          const isCurrent = isCurrentBookingSlot(slot.value);
                          const disabled = isSlotDisabled(slot.value, slot);
                          const booked = isSlotBookedByOther(slot.value, slot);
                          return (
                            <button
                              key={slot.value}
                              type="button"
                              disabled={disabled}
                              className={`time-chip ${disabled ? 'disabled' : ''} ${booked ? 'booked' : ''} ${
                                isCurrent ? 'current-booking' : ''
                              } ${selectedTimeSlot === slot.value && !disabled ? 'selected' : ''}`}
                              onClick={() => {
                                if (!disabled) setSelectedTimeSlot(slot.value);
                              }}
                              title={
                                isCurrent
                                  ? 'Current booking time (This Booking)'
                                  : booked
                                    ? slot.clientName || 'Already booked with another client'
                                    : undefined
                              }
                            >
                              <span>{slot.display}</span>
                              {isCurrent ? (
                                <span className="booked-tag current-tag">This Booking</span>
                              ) : booked ? (
                                <span className="booked-tag">Booked</span>
                              ) : null}
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Action CTA */}
            <Button
              className="btn-submit-suggest"
              onClick={handleSuggestSubmit}
              disabled={isSubmitDisabled}
              isLoading={suggestMutation.isPending}
            >
              Suggest New Time
            </Button>
          </div>
        </div>
        {renderSuggestAlreadySentModal()}
      </div>
    );
  }

  if (loading) {
    return (
      <div
        className="checkin-details-container"
        style={{ alignItems: 'center', justifyContent: 'center' }}
      >
        <Spin indicator={<LoadingOutlined style={{ fontSize: 32, color: '#F59E0B' }} spin />} />
      </div>
    );
  }

  const isUnbookedSlot =
    !checkin ||
    (!checkin.isAppoint &&
      !checkin.appointUId &&
      !checkin.client_uid &&
      !checkin.checkinProgress &&
      !checkin.chat_id &&
      !checkin.message_id);

  if (isUnbookedSlot) {
    return (
      <div className="checkin-details-container">
        <div className="not-found-view-container">
          <div className="not-found-icon-wrapper">
            <EmptyCheckinsIcon />
          </div>
          <h2>{!checkin ? 'Check-in Not Found' : 'Unbooked Availability Slot'}</h2>
          <p>
            {!checkin
              ? 'The requested check-in could not be found or may have been removed.'
              : 'This time slot is open for bookings. No student has reserved this session yet.'}
          </p>
          <button className="btn-back-checkins" onClick={onBack}>
            Back to Check-ins
          </button>
        </div>
      </div>
    );
  }

  // 3. Main Check-in View (Confirmed, Completed, or Pending Request)
  return (
    <div className="checkin-details-container">
      {/* Header Bar */}
      <div className="details-header">
        <div className="header-left">
          <button className="back-button" onClick={onBack} aria-label="Back to Check-ins">
            <ChatBackArrowIcon />
          </button>
          <div
            className={`client-info ${clientUid ? 'clickable' : ''}`}
            onClick={clientUid ? handleClientBannerClick : undefined}
            role={clientUid ? 'button' : undefined}
            tabIndex={clientUid ? 0 : undefined}
            onKeyDown={(e) => {
              if (clientUid && (e.key === 'Enter' || e.key === ' ')) {
                handleClientBannerClick();
              }
            }}
          >
            <div
              className="client-avatar"
              style={{
                backgroundImage: clientAvatar ? `url(${clientAvatar})` : 'none',
              }}
            >
              {!clientAvatar && clientInitials}
            </div>
            <div className="client-name-email">
              <h2>{clientName}</h2>
              {clientEmail && <span>{clientEmail}</span>}
            </div>
          </div>
        </div>

        <div className={`status-badge ${statusBadgeInfo.className}`}>
          {statusBadgeInfo.hasDot && <span className="status-dot" />}
          <span>{statusBadgeInfo.label}</span>
        </div>
      </div>

      <div className="details-body">
        {/* ============================================================ */}
        {/* VIEW A: CONFIRMED CHECK-IN DASHBOARD VIEW                     */}
        {/* ============================================================ */}
        {(status === 'confirmed' || checkinProgress === '3') && (
          <div className="checkin-dashboard-grid">
            {/* Left Column */}
            <div className="dashboard-column column-left">
              {/* Card 1: Check-in Details */}
              <div className="dashboard-card checkin-info-card">
                <div className="card-header">
                  <span className="card-header-icon">
                    <ConfirmedDetailsIcon />
                  </span>
                  <h3>Check-in details</h3>
                </div>
                <div className="details-table">
                  <div className="table-row">
                    <span className="label">Date</span>
                    <span className="val">{formattedDate || '—'}</span>
                  </div>
                  <div className="table-row">
                    <span className="label">Time</span>
                    <span className="val">{formattedTime || '—'}</span>
                  </div>
                  <div className="table-row">
                    <span className="label">Meeting</span>
                    <span className="val">{preferenceLabel || '—'}</span>
                  </div>
                  <div className="table-row">
                    <span className="label">Duration</span>
                    <span className="val">
                      {checkin?.duration_mins ? `${checkin.duration_mins} min` : '30 min'}
                    </span>
                  </div>
                </div>
              </div>

              {/* Card 2: Meeting Access */}
              <div className="dashboard-card meeting-access-card">
                <div className="meeting-access-header">
                  <div className="title-row">
                    <span className="card-header-icon">
                      <ConfirmedMeetingAccessIcon />
                    </span>
                    <h3>Meeting access</h3>
                  </div>
                  <p className="card-subtitle">Join the meeting using the details below</p>
                </div>

                <div className="meeting-access-inner-box">
                  <div className="zoom-link-container">
                    <div className="zoom-link-left">
                      <span className="link-title">Zoom link (auto-generated)</span>
                      {checkin?.zoom_link ? (
                        <a
                          href={checkin.zoom_link}
                          target="_blank"
                          rel="noreferrer"
                          className="zoom-url-text"
                        >
                          {checkin.zoom_link}
                        </a>
                      ) : (
                        <span className="zoom-url-text" style={{ color: '#8e95a5' }}>
                          No Zoom link available
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      disabled={!checkin?.zoom_link}
                      className={`btn-copy-link ${isCopiedZoomLink ? 'copied-btn' : ''}`}
                      onClick={handleCopyZoomLink}
                    >
                      {isCopiedZoomLink ? (
                        <CheckOutlined style={{ color: '#22c55e' }} />
                      ) : (
                        <ConfirmedCopyLinkIcon />
                      )}{' '}
                      {isCopiedZoomLink ? 'Copied' : 'Copy link'}
                    </button>
                  </div>

                  <div className="meeting-credentials">
                    <div className="credential-row">
                      <span className="label">Meeting ID</span>
                      <span className="val">{meetingId || '—'}</span>
                    </div>
                    <div className="credential-row">
                      <span className="label">Passcode</span>
                      <span className="val">{passcode || '—'}</span>
                    </div>
                  </div>

                  <button
                    type="button"
                    disabled={!checkin?.zoom_link}
                    className="btn-open-zoom"
                    onClick={() => {
                      if (checkin?.zoom_link) {
                        window.open(checkin.zoom_link, '_blank');
                      }
                    }}
                  >
                    Open Zoom <ConfirmedOpenZoomIcon />
                  </button>
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="dashboard-column column-right">
              {/* Card 1: AI Progress Summary Before Meeting */}
              <div className="dashboard-card ai-progress-summary-card">
                <div className="ai-progress-header">
                  <div className="title-row">
                    <span className="card-header-icon round-sparkle-badge">
                      <ConfirmedAiProgressIcon />
                    </span>
                    <h3>AI progress summary before meeting</h3>
                  </div>
                  <p className="card-subtitle">
                    Here&apos;s what {clientName ? clientName.split(' ')[0] : 'the client'} has been
                    up to since your last check-in.
                  </p>
                </div>

                <div className="bento-summary-grid">
                  {/* Bento 1: Performance Overview */}
                  <div className="bento-cell performance-cell">
                    <div className="bento-cell-header">
                      <span className="cell-icon-badge badge-green">
                        <ConfirmedPerformanceOverviewIcon />
                      </span>
                      <h4>Performance Overview</h4>
                    </div>
                    <div className="performance-content">
                      <div className="donut-chart-container">
                        <svg className="donut-svg" viewBox="0 0 100 100">
                          <circle
                            className="donut-bg"
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="rgba(255,255,255,0.06)"
                            strokeWidth="7"
                          />
                          <circle
                            className="donut-fill"
                            cx="50"
                            cy="50"
                            r="40"
                            fill="none"
                            stroke="#71BE41"
                            strokeWidth="7"
                            strokeDasharray="251.33"
                            strokeDashoffset={
                              progressSummary?.performance_score !== undefined
                                ? 251.33 - (251.33 * progressSummary.performance_score) / 100
                                : 251.33
                            }
                            strokeLinecap="round"
                            transform="rotate(-90 50 50)"
                          />
                        </svg>
                        <div className="donut-label">
                          <span className="donut-pct">
                            {progressSummary?.performance_score !== undefined
                              ? `${progressSummary.performance_score}%`
                              : '—'}
                          </span>
                          <span className="donut-sub">Overall Score</span>
                        </div>
                      </div>
                      <div className="performance-legend">
                        <div className="legend-row">
                          <span className="dot dot-green" />
                          <span className="legend-name">Great Form</span>
                          <span className="legend-num">
                            {progressSummary?.great_form_count ?? 0}
                          </span>
                        </div>
                        <div className="legend-row">
                          <span className="dot dot-lightgreen" />
                          <span className="legend-name">Good Form</span>
                          <span className="legend-num">
                            {progressSummary?.good_form_count ?? 0}
                          </span>
                        </div>
                        <div className="legend-row">
                          <span className="dot dot-amber" />
                          <span className="legend-name">Needs Focus</span>
                          <span className="legend-num">
                            {progressSummary?.needs_focus_count ?? 0}
                          </span>
                        </div>
                        <div className="legend-row">
                          <span className="dot dot-red" />
                          <span className="legend-name">Skipped</span>
                          <span className="legend-num">{progressSummary?.skipped_count ?? 0}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Bento 2: Workouts Completed */}
                  <div className="bento-cell workouts-cell">
                    <div className="bento-cell-header">
                      <span className="cell-icon-badge badge-purple">
                        <ConfirmedWorkoutCompletedIcon />
                      </span>
                      <h4>Workouts completed</h4>
                    </div>
                    <div className="workouts-content">
                      <div className="mini-bars-chart">
                        {[
                          { day: 'M', active: false, height: 12 },
                          { day: 'T', active: true, height: 26 },
                          { day: 'W', active: false, height: 8 },
                          { day: 'T', active: true, height: 26 },
                          { day: 'F', active: false, height: 8 },
                          { day: 'S', active: false, height: 16 },
                          { day: 'S', active: false, height: 10 },
                        ].map((bar, idx) => (
                          <div key={idx} className="bar-column">
                            <div
                              className={`bar-fill ${bar.active ? 'active' : ''}`}
                              style={{ height: `${bar.height}px` }}
                            />
                            <span className="bar-day">{bar.day}</span>
                          </div>
                        ))}
                      </div>
                      <div className="workouts-stat">
                        <span className="stat-bold">
                          {progressSummary?.workouts_completed ?? 0}{' '}
                          <span className="stat-regular">of</span>{' '}
                          {progressSummary?.workouts_total ?? 0}
                        </span>
                        <span className="stat-muted">this week</span>
                      </div>
                    </div>
                  </div>

                  {/* Bento 3: Last Activity */}
                  <div className="bento-cell last-activity-cell">
                    <div className="bento-cell-header">
                      <span className="cell-icon-badge badge-cyan">
                        <ConfirmedLastActivityIcon />
                      </span>
                      <h4>Last activity</h4>
                    </div>
                    <div className="cell-single-stat">
                      <span className="stat-highlight">
                        {progressSummary?.last_activity_date || 'No recent activity'}{' '}
                        {progressSummary?.last_activity_relative && (
                          <span className="stat-sub">
                            ({progressSummary.last_activity_relative})
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  {/* Bento 4: Form Concern */}
                  <div className="bento-cell form-concern-cell">
                    <div className="bento-cell-header">
                      <span className="cell-icon-badge badge-red">
                        <ConfirmedFormConcernIcon />
                      </span>
                      <h4>Form concern</h4>
                    </div>
                    <div className="cell-single-stat">
                      <span className="stat-highlight">
                        {progressSummary?.form_concern || 'None reported'}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="dashboard-card next-actions-card">
                <div className="section-header-block">
                  <div className="title-row">
                    <span className="card-header-icon">
                      <ConfirmedNextActionsIcon />
                    </span>
                    <h3>Next actions</h3>
                  </div>
                  <p className="card-subtitle">
                    Prepare for your meeting and keep{' '}
                    {clientName ? clientName.split(' ')[0] : 'the client'} on track.
                  </p>
                </div>

                <div className="actions-button-row">
                  <button
                    type="button"
                    className="btn-action-complete"
                    onClick={handleOpenCompleteModal}
                    disabled={completeMutation.isPending}
                  >
                    <CheckOutlined /> Complete check-in
                  </button>
                  <button
                    type="button"
                    className="btn-action-reminder"
                    onClick={handleSendReminder}
                    disabled={sendReminderMutation.isPending}
                  >
                    {sendReminderMutation.isPending ? (
                      <LoadingOutlined />
                    ) : (
                      <ConfirmedSendReminderIcon />
                    )}{' '}
                    {sendReminderMutation.isPending ? 'Sending...' : 'Send Reminder'}
                  </button>
                  <button
                    type="button"
                    className="btn-action-reschedule"
                    onClick={handleOpenReschedule}
                  >
                    <ConfirmedRescheduleIcon /> Reschedule check-in
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* VIEW B: COMPLETED CHECK-IN DASHBOARD VIEW                    */}
        {/* ============================================================ */}
        {(status === 'completed' || checkinProgress === '4') && (
          <div className="checkin-dashboard-grid">
            {/* Left Column */}
            <div className="dashboard-column column-left">
              {/* Card 1: Check-in Details */}
              <div className="dashboard-card checkin-info-card">
                <div className="card-header">
                  <span className="card-header-icon">
                    <ConfirmedDetailsIcon />
                  </span>
                  <h3>Check-in details</h3>
                </div>
                <div className="details-table">
                  <div className="table-row">
                    <span className="label">Date</span>
                    <span className="val">{formattedDate}</span>
                  </div>
                  <div className="table-row">
                    <span className="label">Time</span>
                    <span className="val">{formattedTime}</span>
                  </div>
                  <div className="table-row">
                    <span className="label">Meeting</span>
                    <span className="val">{preferenceLabel}</span>
                  </div>
                  <div className="table-row">
                    <span className="label">Duration</span>
                    <span className="val">
                      {checkin?.duration_mins ? `${checkin.duration_mins} min` : '32 min'}
                    </span>
                  </div>
                  <div className="table-row">
                    <span className="label">Attendance</span>
                    <span className="val">
                      <span className="attendance-pill">
                        {checkin?.attendance === 'missed' ? 'Missed' : 'Attended'}
                      </span>
                    </span>
                  </div>
                </div>
              </div>

              {/* Card 2: Transcript Highlights */}
              <div className="dashboard-card transcript-highlights-card">
                <div className="card-header-flex">
                  <div className="card-header-left">
                    <span className="card-header-icon">
                      <ConfirmedTranscriptionIcon />
                    </span>
                    <h3>Transcript highlights</h3>
                  </div>
                  {fullTranscriptDialogue.length > 0 && (
                    <button
                      type="button"
                      className="btn-view-full-transcript"
                      onClick={() => setIsFullTranscriptModalOpen(true)}
                    >
                      View full transcript <RightOutlined style={{ fontSize: 11 }} />
                    </button>
                  )}
                </div>

                {highlights.length > 0 ? (
                  <div className="highlights-list">
                    {highlights.map((item, index) => (
                      <div key={index} className="highlight-entry">
                        <span className="timestamp">{item.time}</span>
                        <span className="entry-text">{item.text}</span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="empty-transcript-state">
                    <span className="card-header-icon empty-icon-wrap">
                      <ConfirmedTranscriptionIcon />
                    </span>
                    <p className="empty-text">No Transcript data</p>
                    <span className="empty-subtext">
                      No transcript recording available for this session.
                    </span>
                  </div>
                )}
              </div>
            </div>

            {/* Right Column */}
            <div className="dashboard-column column-right">
              {/* Card 1: AI Check-in Summary */}
              <div className="dashboard-card ai-checkin-summary-card">
                <div className="card-header">
                  <span className="card-header-icon round-sparkle-badge">
                    <ConfirmedAiProgressIcon />
                  </span>
                  <h3>AI check-in summary</h3>
                </div>
                {aiSummaryParagraphs.length > 0 ? (
                  <div className="summary-paragraphs">
                    {aiSummaryParagraphs.map((para, idx) => (
                      <p key={idx}>{para}</p>
                    ))}
                  </div>
                ) : (
                  <div className="empty-summary-state">
                    <p className="empty-subtext">No AI summary generated for this check-in.</p>
                  </div>
                )}
              </div>

              {/* Card 2: Action Items */}
              <div className="dashboard-card action-items-card">
                <div className="card-header">
                  <span className="card-header-icon">
                    <CompletedActionItemsIcon />
                  </span>
                  <h3>Action items</h3>
                </div>
                {currentActionItems.length > 0 ? (
                  <div className="action-items-list">
                    {currentActionItems.map((item) => (
                      <label key={item.id} className="action-checkbox-item">
                        <input
                          type="checkbox"
                          checked={actionItemsChecked[item.id]}
                          onChange={() => toggleActionItem(item.id)}
                        />
                        <span className="custom-checkbox">
                          {actionItemsChecked[item.id] && (
                            <CheckOutlined style={{ fontSize: 10 }} />
                          )}
                        </span>
                        <span
                          className={`item-label ${actionItemsChecked[item.id] ? 'checked' : ''}`}
                        >
                          {item.text}
                        </span>
                      </label>
                    ))}
                  </div>
                ) : (
                  <div className="empty-action-items-state">
                    <p className="empty-subtext">No action items recorded for this check-in.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* VIEW C: PROPOSED NEW TIME (checkinProgress === '2')           */}
        {/* ============================================================ */}
        {checkinProgress === '2' && (
          <div className="details-center-panel">
            <div
              className={`checkin-proposal-banner ${
                checkin?.checkinNewSuggest ? 'counter-proposal' : ''
              }`}
            >
              <div className="banner-title">
                {checkin?.checkinNewSuggest ? 'Client Requested Another Time' : 'New Time Proposed'}
              </div>
              <div className="banner-desc">
                {checkin?.checkinNewSuggest
                  ? 'The client received your proposed time and requested that you suggest a different date or time.'
                  : 'You have proposed an alternative time for this session. Awaiting confirmation from the client.'}
              </div>
            </div>

            <div className="info-card">
              <div className="info-row">
                <span className="info-label">Client</span>
                <span className="info-val">{clientName}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Original Request</span>
                <span className="info-val">
                  {formattedDate} at {formattedTime}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Proposed Date</span>
                <span className="info-val" style={{ color: '#F59E0B', fontWeight: 600 }}>
                  {checkin?.checkinNewDate || '—'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Proposed Time</span>
                <span className="info-val" style={{ color: '#F59E0B', fontWeight: 600 }}>
                  {checkin?.checkinNewTime ? formatTimeWithAmPm(checkin.checkinNewTime) : '—'}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Session Type</span>
                <span className="info-val">
                  {checkin?.checkinNewType === 'inPerson' ? 'In-person' : 'Online via Zoom'}
                </span>
              </div>
            </div>

            <div className="actions-container">
              <button
                className="btn-suggest"
                onClick={handleOpenReschedule}
                disabled={suggestMutation.isPending || declineMutation.isPending}
              >
                <CheckinSuggestIcon />
                Suggest Another Time
              </button>

              <button
                className="btn-decline"
                onClick={() => setIsDeclineModalOpen(true)}
                disabled={suggestMutation.isPending || declineMutation.isPending}
              >
                <CheckinDeclineIcon />
                Decline Request
              </button>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* VIEW D: DECLINED CHECK-IN (checkinProgress === '5' or status) */}
        {/* ============================================================ */}
        {(checkinProgress === '5' || (status === 'declined' && checkinProgress !== '6')) && (
          <div className="details-center-panel">
            <div className="checkin-status-callout declined">
              <div className="callout-header">
                <CloseOutlined style={{ color: '#EF4444' }} />
                <h3>Check-in Request Declined</h3>
              </div>
              <p className="callout-desc">
                This check-in request was declined and the calendar slot has been freed for new
                bookings.
              </p>
              {(checkin?.checkinDeclinedReason || checkin?.decline_reason) && (
                <div className="callout-reason-box">
                  <span className="reason-label">Reason provided:</span>
                  <p className="reason-text">
                    &ldquo;{checkin.checkinDeclinedReason || checkin.decline_reason}&rdquo;
                  </p>
                </div>
              )}
            </div>

            <div className="info-card">
              <div className="info-row">
                <span className="info-label">Client</span>
                <span className="info-val">{clientName}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Requested Date</span>
                <span className="info-val">{formattedDate}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Requested Time</span>
                <span className="info-val">{formattedTime}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Preference</span>
                <span className="info-val">{preferenceLabel}</span>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* VIEW E: CANCELLED CHECK-IN (checkinProgress === '6')         */}
        {/* ============================================================ */}
        {checkinProgress === '6' && (
          <div className="details-center-panel">
            <div className="checkin-status-callout cancelled">
              <div className="callout-header">
                <CloseOutlined style={{ color: '#9CA3AF' }} />
                <h3>Check-in Cancelled</h3>
              </div>
              <p className="callout-desc">
                The client cancelled this check-in request. The calendar slot is now open for new
                bookings.
              </p>
            </div>

            <div className="info-card">
              <div className="info-row">
                <span className="info-label">Client</span>
                <span className="info-val">{clientName}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Original Date</span>
                <span className="info-val">{formattedDate}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Original Time</span>
                <span className="info-val">{formattedTime}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Preference</span>
                <span className="info-val">{preferenceLabel}</span>
              </div>
            </div>
          </div>
        )}

        {/* ============================================================ */}
        {/* VIEW F: PENDING CONFIRMATION / DEFAULT CARD VIEW             */}
        {/* ============================================================ */}
        {(checkinProgress === '1' || !checkinProgress) && status === 'pending_confirmation' && (
          <div className="details-center-panel">
            {/* Card 1: Details Table */}
            <div className="info-card">
              <div className="info-row">
                <span className="info-label">Client</span>
                <span className="info-val">{clientName}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Date</span>
                <span className="info-val">{formattedDate}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Time</span>
                <span className="info-val">{formattedTime}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Preference</span>
                <span className="info-val">{preferenceLabel}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Duration</span>
                <span className="info-val">
                  {checkin?.duration_mins ? `${checkin.duration_mins} min` : '30 min'}
                </span>
              </div>
            </div>

            {/* Card 2: Zoom Toggle Card */}
            <div className="toggle-card">
              <span className="toggle-label">
                Send Zoom link automatically
                <br />
                on confirmation
              </span>
              <Switch
                className="custom-switch"
                checked={sendZoomLink}
                onChange={(checked) => setSendZoomLink(checked)}
                disabled={
                  confirmMutation.isPending ||
                  suggestMutation.isPending ||
                  declineMutation.isPending
                }
              />
            </div>

            {/* Actions Container */}
            <div className="actions-container">
              <button
                className="btn-confirm"
                onClick={handleConfirmClick}
                disabled={
                  confirmMutation.isPending ||
                  suggestMutation.isPending ||
                  declineMutation.isPending
                }
              >
                <CheckinConfirmIcon />
                {confirmMutation.isPending ? 'Confirming...' : 'Confirm check-in'}
              </button>

              <button
                className="btn-suggest"
                onClick={handleOpenReschedule}
                disabled={
                  confirmMutation.isPending ||
                  suggestMutation.isPending ||
                  declineMutation.isPending
                }
              >
                <CheckinSuggestIcon />
                Suggest New Time
              </button>

              <button
                className="btn-decline"
                onClick={() => setIsDeclineModalOpen(true)}
                disabled={
                  confirmMutation.isPending ||
                  suggestMutation.isPending ||
                  declineMutation.isPending
                }
              >
                <CheckinDeclineIcon />
                Decline Request
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Decline Request Confirmation Modal */}
      <Modal
        open={isDeclineModalOpen}
        footer={null}
        onCancel={handleCloseDeclineModal}
        centered
        width={440}
        closable={false}
        rootClassName="logout-modal decline-checkin-modal-root"
        className="logout-modal decline-checkin-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="decline-modal-content">
          <div className="decline-modal-header">
            <h2 className="modal-title">Decline Request</h2>
            <button
              type="button"
              className="close-btn"
              onClick={handleCloseDeclineModal}
              disabled={declineMutation.isPending}
              aria-label="Close"
            >
              <CloseOutlined />
            </button>
          </div>

          <div className="decline-modal-body">
            <label className="input-label" htmlFor="decline-message-input">
              Add message (optional)
            </label>
            <div className={`textarea-container ${declineReasonError ? 'has-error' : ''}`}>
              <textarea
                id="decline-message-input"
                placeholder="Type here..."
                value={declineReason}
                maxLength={200}
                rows={4}
                onChange={(e) => {
                  const val = e.target.value;
                  if (val.length <= 200) {
                    setDeclineReason(val);
                    if (declineReasonError) {
                      setDeclineReasonError('');
                    }
                  }
                }}
                onBlur={(e) => {
                  const trimmed = e.target.value.trim();
                  setDeclineReason(trimmed);
                  const err = validateDeclineReason(trimmed);
                  setDeclineReasonError(err);
                }}
                disabled={declineMutation.isPending}
              />
              <div className="char-counter">{declineReason.length}/200</div>
            </div>
            {declineReasonError && <span className="inline-error-text">{declineReasonError}</span>}
          </div>

          <div className="decline-modal-actions">
            <button
              type="button"
              className="btn-keep-it"
              onClick={handleCloseDeclineModal}
              disabled={declineMutation.isPending}
            >
              Keep It
            </button>
            <button
              type="button"
              className="btn-decline-confirm"
              onClick={handleDeclineSubmit}
              disabled={declineMutation.isPending || Boolean(declineReasonError)}
            >
              {declineMutation.isPending ? (
                <Spin
                  indicator={<LoadingOutlined style={{ fontSize: 16, color: '#FFA100' }} spin />}
                />
              ) : (
                'Decline'
              )}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirm Check-in (when request already sent) Confirmation Modal */}
      <Modal
        open={isConfirmAlreadySentModalOpen}
        footer={null}
        onCancel={() => setIsConfirmAlreadySentModalOpen(false)}
        centered
        width={400}
        closeIcon={null}
        rootClassName="logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="logout-modal-content">
          <div className="logout-icon-wrapper">
            <DashboardModalArchiveIcon />
          </div>
          <h2>Confirm Check-in?</h2>
          <p>
            You have already proposed a new time for this check-in to{' '}
            <b>{checkin?.client_name || 'the client'}</b>. Do you want to proceed and confirm this
            check-in session for{' '}
            <b>
              {formattedDate} at {formattedTime}
            </b>
            ?
          </p>
          <div className="logout-actions">
            <Button
              className="btn-logout"
              onClick={executeConfirm}
              disabled={confirmMutation.isPending}
              isLoading={confirmMutation.isPending}
            >
              Confirm Check-in
            </Button>
            <Button
              className="btn-cancel"
              onClick={() => setIsConfirmAlreadySentModalOpen(false)}
              disabled={confirmMutation.isPending}
            >
              Cancel
            </Button>
          </div>
        </div>
      </Modal>

      {/* Send Suggestion (when request already sent) Confirmation Modal */}
      {renderSuggestAlreadySentModal()}

      {/* Full Transcript Popup Modal */}
      <Modal
        open={isFullTranscriptModalOpen}
        onCancel={() => setIsFullTranscriptModalOpen(false)}
        footer={null}
        centered
        width={680}
        rootClassName="full-transcript-modal-root"
        wrapClassName="full-transcript-modal-wrap"
        className="full-transcript-modal-root"
        styles={{
          mask: {
            backdropFilter: 'blur(6px)',
            backgroundColor: 'rgba(0, 0, 0, 0.75)',
          },
        }}
        modalRender={() => (
          <div className="full-transcript-modal-wrapper">
            <div className="full-transcript-dialog">
              <div className="dialog-header">
                <div className="header-text-group">
                  <h2>Full Transcript</h2>
                  <span className="transcript-duration">32:45,32</span>
                </div>
                <button
                  type="button"
                  className="btn-modal-close"
                  onClick={() => setIsFullTranscriptModalOpen(false)}
                  aria-label="Close"
                >
                  <CloseOutlined />
                </button>
              </div>
              <div className="dialog-body custom-dark-scrollbar">
                {fullTranscriptDialogue.length > 0 ? (
                  fullTranscriptDialogue.map((paragraph, index) => (
                    <p key={index} className="transcript-paragraph">
                      {paragraph}
                    </p>
                  ))
                ) : (
                  <div className="empty-transcript-state modal-empty">
                    <FileTextOutlined className="empty-icon" />
                    <p className="empty-text">No Transcript data</p>
                    <span className="empty-subtext">
                      No full transcript recording available for this session.
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      />

      {/* Complete Check-in Modal */}
      <Modal
        open={isCompleteModalOpen}
        footer={null}
        onCancel={() => setIsCompleteModalOpen(false)}
        centered
        width={540}
        closeIcon={null}
        rootClassName="complete-checkin-modal logout-modal"
        className="logout-modal"
        styles={{
          mask: {
            backdropFilter: 'blur(4px)',
            backgroundColor: 'rgba(6, 7, 35, 0.8)',
          },
        }}
      >
        <div className="complete-modal-content">
          <div className="complete-modal-header">
            <div className="header-title-block">
              <h2 className="modal-title">Complete Check-in</h2>
              <p className="modal-subtitle">
                Wrap up your session with <b>{clientName}</b>
              </p>
            </div>
            <button
              type="button"
              className="close-btn"
              onClick={() => setIsCompleteModalOpen(false)}
              aria-label="Close"
            >
              <CloseOutlined />
            </button>
          </div>

          {isZoomCheckin && (
            <div className="zoom-sync-card">
              {isZoomStatusLoading ? (
                <div className="zoom-sync-loading">
                  <Spin
                    indicator={<LoadingOutlined style={{ fontSize: 16, color: '#f59e0b' }} spin />}
                  />
                  <span>Connecting to Zoom & checking session details...</span>
                </div>
              ) : zoomStatus?.has_transcript ? (
                <div className="zoom-sync-status detected">
                  <div className="status-top">
                    <span className="pulse-dot green" />
                    <span className="sync-title">Zoom Session Detected</span>
                    {zoomStatus.actual_duration_mins && (
                      <span className="duration-tag">{zoomStatus.actual_duration_mins} min</span>
                    )}
                  </div>
                  <p className="sync-desc">
                    Cloud transcript and highlights found ({zoomStatus.transcript_turns_count || 0}{' '}
                    dialogue turns). Actual duration auto-filled below.
                  </p>
                  {zoomStatus.transcript_preview && (
                    <div className="sync-preview">
                      <span className="preview-label">Preview:</span> &quot;
                      {zoomStatus.transcript_preview}&quot;
                    </div>
                  )}
                </div>
              ) : zoomStatus?.actual_duration_mins ? (
                <div className="zoom-sync-status duration-only">
                  <div className="status-top">
                    <span className="pulse-dot green" />
                    <span className="sync-title">Zoom Session Recorded</span>
                    <span className="duration-tag">{zoomStatus.actual_duration_mins} min</span>
                  </div>
                  <p className="sync-desc">
                    Actual meeting duration detected from Zoom ({zoomStatus.actual_duration_mins}{' '}
                    mins). No cloud audio transcript found.
                  </p>
                </div>
              ) : (
                <div className="zoom-sync-status no-recording">
                  <div className="status-top">
                    <span className="pulse-dot grey" />
                    <span className="sync-title">Zoom Session (Scheduled)</span>
                  </div>
                  <p className="sync-desc">
                    No cloud recording found yet. You can confirm attendance, duration, and notes
                    manually below.
                  </p>
                </div>
              )}
            </div>
          )}

          <div className="form-group">
            <label>ATTENDANCE</label>
            <div className="attendance-toggle-group">
              <button
                type="button"
                className={`pill-btn ${completionAttendance === 'attended' ? 'active' : ''}`}
                onClick={() => setCompletionAttendance('attended')}
              >
                Attended
              </button>
              <button
                type="button"
                className={`pill-btn ${completionAttendance === 'missed' ? 'active' : ''}`}
                onClick={() => setCompletionAttendance('missed')}
              >
                Missed
              </button>
            </div>
          </div>

          <div className="form-group">
            <label>
              SESSION DURATION
              {zoomStatus?.actual_duration_mins ? (
                <span className="optional-tag">(AUTO-DETECTED FROM ZOOM)</span>
              ) : null}
            </label>
            <div className="duration-pill-group">
              {durationOptions.map((mins) => (
                <button
                  key={mins}
                  type="button"
                  className={`pill-btn ${completionDuration === mins ? 'active' : ''}`}
                  onClick={() => setCompletionDuration(mins)}
                >
                  {mins} min
                </button>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>
              SESSION NOTES / SUMMARY <span className="optional-tag">(OPTIONAL)</span>
            </label>
            <textarea
              className="complete-textarea"
              placeholder="Summarize key points, form feedback, or discussion highlights..."
              value={completionNotes}
              onChange={(e) => setCompletionNotes(e.target.value)}
              onBlur={(e) => setCompletionNotes(e.target.value.trim())}
              rows={3}
            />
          </div>

          <div className="form-group">
            <label>
              ACTION ITEMS <span className="optional-tag">(OPTIONAL)</span>
            </label>
            <div className="action-items-input-row">
              <input
                type="text"
                className="complete-input"
                placeholder="e.g. Upload squat form video by Friday"
                value={newActionItemText}
                onChange={(e) => setNewActionItemText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    handleAddActionItem();
                  }
                }}
              />
              <button
                type="button"
                className="btn-add-action-item"
                onClick={handleAddActionItem}
                disabled={!newActionItemText.trim()}
              >
                Add
              </button>
            </div>

            {completionActionItems.length > 0 && (
              <div className="action-items-preview-list">
                {completionActionItems.map((item) => (
                  <div key={item.id} className="action-item-pill">
                    <span>{item.text}</span>
                    <button
                      type="button"
                      className="btn-remove-item"
                      onClick={() => handleRemoveActionItem(item.id)}
                      aria-label="Remove item"
                    >
                      <CloseOutlined />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="modal-actions">
            <Button
              className="btn-cancel"
              onClick={() => setIsCompleteModalOpen(false)}
              disabled={completeMutation.isPending}
            >
              Cancel
            </Button>
            <Button
              className="btn-complete-submit"
              onClick={handleCompleteSubmit}
              disabled={completeMutation.isPending}
              isLoading={completeMutation.isPending}
            >
              Mark as Completed
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
